<template>
  <app-modal
    ref="modal"
    v-bind="$attrs"
    class="modal-container"
    content-class="modal-large"
    title="インターネットアプライアンス ゾーン接続設定"
  >
    <template v-if="isLoaded" v-slot:body="{ cancel }">
      <!-- BODY -->
      <v-card-text class="pa-0">
        <div class="modal-body">
          <div class="d-flex flex-wrap justify-end mt-4">
            <v-btn color="primary" @click="addZoneRelation"
              >ゾーン接続追加
            </v-btn>
          </div>
          <v-alert v-if="!!alertMsg" class="my-2" :text="alertMsg"></v-alert>
          <div class="mt-5">
            <app-table
              primary-key="zoneRelationId"
              :fields="zoneRelations.fields"
              :items="displayRelationList"
              v-model:table-sort-by="zoneRelations.sortBy"
              :per-page="-1"
              :tr-class="() => undefined"
            >
              <template #cell(direction)> → </template>
              <!-- ゾーン接続削除 -->
              <template #cell(delete)="{ item }">
                <v-btn
                  size="small"
                  color="primary"
                  @click="deleteZoneRelation(item)"
                  >ゾーン接続削除
                </v-btn>
              </template>
            </app-table>
          </div>
        </div>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions>
        <v-list-item class="w-100 pa-1">
          <template v-slot:append>
            <v-btn variant="outlined" color="primary" @click="cancel">
              戻る
            </v-btn>
          </template>
        </v-list-item>
      </v-card-actions>
    </template>
  </app-modal>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import type { ZoneRelation, ZoneRelationItem } from "@/apis/ZoneRelationApi";
import type { Type1Zone } from "@/apis/Type1SiteApi";
import type { AppTableHeader, SortItem } from "@/components/AppTable.vue";
import InternetType1ZoneRelationDeleteConfirm from "@/modals/applianceContractSetting/type1/zoneRelationSetting/InternetType1ZoneRelationDeleteConfirm.vue";
import InternetType1ZoneRelationRegister from "@/modals/applianceContractSetting/type1/zoneRelationSetting/InternetType1ZoneRelationRegister.vue";

export default defineComponent({
  name: "InternetType1ZoneRelationSettingList",
  data() {
    return {
      /** 初期通信完了フラグ */
      isLoaded: false,
      relations: {} as ZoneRelation,
      zoneRelations: {
        sortBy: { key: "zone_src", order: "asc" } as SortItem,
        fields: [
          {
            key: "zone_src",
            title: "送信元ゾーン/ゾーン名",
            sortable: true,
            sortByFormatted: true,
            formatter: (_v, _k, item: ZoneRelationItem) => {
              return (
                this.$filter.enumTo(item.srcZoneType, "zoneType") +
                "/" +
                item.srcZoneName
              );
            },
          },
          {
            key: "direction",
            title: "",
          },
          {
            key: "zone_dest",
            title: "宛先ゾーン/ゾーン名",
            sortable: true,
            sortByFormatted: true,
            formatter: (_v, _k, item: ZoneRelationItem) => {
              return (
                this.$filter.enumTo(item.dstZoneType, "zoneType") +
                "/" +
                item.dstZoneName
              );
            },
          },
          { key: "delete", title: "" },
        ] as AppTableHeader<ZoneRelationItem>[],
      },
      // インターネット、エクストラ、プライベートゾーン
      internetZone: {} as Type1Zone | null,
      extraZone: {} as Type1Zone | null,
      privateZone: [] as Type1Zone[],
      alertMsg: null as null | string,
    };
  },
  async mounted() {
    await this.load();
    this.isLoaded = true;
  },
  computed: {
    displayRelationList(): ZoneRelationItem[] {
      // global -> private
      // global -> extra
      // extra -> global
      // private -> global
      // 上記のみ表示
      return this.relations.zoneRelationList.filter(
        (v) =>
          (v.srcZoneType === "INTERNET" &&
            (v.dstZoneType === "PRIVATE" || v.dstZoneType === "EXTRA")) ||
          ((v.srcZoneType === "PRIVATE" || v.srcZoneType === "EXTRA") &&
            v.dstZoneType === "INTERNET")
      );
    },
  },
  methods: {
    /** データ取得 */
    async load() {
      this.relations = await this.$api.zoneRelation.getZoneRelation();
      this.internetZone = (
        await this.$api.type1SiteZone.getType1InternetList()
      ).zone;
      this.extraZone = (await this.$api.type1SiteZone.getType1ExtraList()).zone;
      this.privateZone = (
        await this.$api.type1SiteZone.getType1PrivateList()
      ).zoneList;
    },
    async deleteZoneRelation(item: ZoneRelationItem) {
      this.alertMsg = null;
      await this.$modal.show(InternetType1ZoneRelationDeleteConfirm, {
        zoneRelationItem: item,
      });
      await this.$api.zoneRelation.deleteZoneRelation(item.zoneRelationId, {
        zoneRelationKeyId: this.relations.zoneRelationKeyId,
      });
      await this.load();
    },
    async addZoneRelation() {
      if (
        this.internetZone === null ||
        (this.extraZone === null && this.privateZone.length === 0)
      ) {
        // ゾーン作成時にsrc/dstのどちらかはインターネットゾーンが必要
        // ゾーン作成時にsrc/dstどちらかはエクストラまたはプライベーとが必要
        this.alertMsg = "ゾーン接続可能なゾーンがありません";
        return;
      } else {
        this.alertMsg = null;
      }

      await this.$modal.show(InternetType1ZoneRelationRegister, {
        zoneRelationKeyId: this.relations.zoneRelationKeyId,
        relations: this.relations,
      });
      await this.load();
    },
  },
});
</script>
